/**
 * Shared types for ``@pervasivemind/dashboard-render`` consumers.
 *
 * Kept structural and minimal — these are the shapes the package's
 * widgets (DataTable, GenericFilterBar, GenericChart) need from app
 * code. Each app may have a richer local type that EXTENDS these via
 * intersection; the package only relies on what it consumes.
 */

import type { DisplayLabel, LocalizedString } from "./i18n-resolve";

/** The tone vocabulary (look v3, DQ2): the only way a report config names
 *  the colour of a state; the component paints the v3 colour of the tone.
 *  Mirrors ``report_engine.discovery._TONES`` and bi-components ``TONES``. */
export type ConfigTone = "good" | "warn" | "bad" | "info" | "info_light" | "neutral" | "muted";

/**
 * Iter 20 — chat-LLM lexicon hints. Optional bilingual synonyms
 * attached to a filter / KPI / chart / table column so the chat
 * agent can resolve free-text like "filtra por mercado" → the
 * ``country`` filter even when the literal word doesn't appear in
 * the label.
 */
export interface Synonyms {
  en?: string[];
  es?: string[];
}

/**
 * Filter row in a dashboard's global / per-section filter bar.
 *
 * The shape matches ``packages/report-engine``'s
 * ``FilterConfig`` dataclass serialized to JSON, with the optional
 * fields the builder UI may write back (``label``, ``synonyms``,
 * ``column_type``, ``timezone``). Components in this package only
 * read it — never mutate.
 */
export interface DashboardFilter {
  key: string;
  column: string;
  type?: string;
  required?: boolean;
  /** null/omitted: legacy symmetric cascade; []: independent root. */
  depends_on?: string[] | null;
  extra_options?: string[];
  locked?: string | null;
  default_value?: string | null;
  /** Optional static, authored option list. For ``multi_select`` / ``chip``
   *  this is the curated set of column values shown by the picker. A
   *  ``multi_select`` without authored options is dynamic and receives
   *  its cascaded choices from ``/filters``. */
  options?: string[] | null;
  /** Per-option display labels (value → DisplayLabel). The picker, the
   *  tabs and the active chip show the label; the SQL keeps the value.
   *  Absent/empty = the value is its own label. */
  option_labels?: Record<string, DisplayLabel> | null;
  /** ``multi_select`` over ``"Attribute · Value"`` tags (engine
   *  ``facet_tags``): the drawer draws a titled section with one picker
   *  per attribute and the chips name each attribute. See
   *  ``facet-tags.ts``. */
  facet_tags?: boolean;
  /** CHS 2026-07 — render-only: a pinned filter shows as an
   *  always-visible segmented control OUTSIDE the collapsible filter
   *  header; the drawer renders a dropdown for the same key. Ignored
   *  by the classic banner bar layout. */
  pinned?: boolean;
  /** Render-only (DSA): the dashboard tab ids this filter is SHOWN on;
   *  empty/absent = every tab. A hidden filter still applies to the
   *  query — the host just does not draw the control (nor its chip)
   *  while another tab is active. See ``filter-tabs.ts``. */
  tabs?: string[] | null;
  /** Optional human-readable label. ``DisplayLabel`` = string | {en, es}. */
  label?: DisplayLabel | null;
  /**
   * Optional help text authored alongside the label. Same
   * ``DisplayLabel`` shape, same defensive-resolve requirement.
   */
  description?: DisplayLabel | null;
  /** Iter 20 — chat-LLM synonym hints. */
  /** Row caption under ``filters_layout: "rows"`` (DSA 1.6): filters
   *  sharing a group render on one labelled row, in filter order. */
  group?: DisplayLabel | null;
  synonyms?: Synonyms | null;
  /**
   * Cached DuckDB column type — drives whether ``DateRangePicker``
   * surfaces time controls. Not load-bearing; the backend re-probes
   * at query time.
   */
  column_type?: string | null;
  /**
   * IANA zone (e.g. ``"America/Argentina/Buenos_Aires"``) for preset
   * resolution (``today``, ``last_30_days``). Omit for UTC.
   */
  timezone?: string | null;
  /**
   * Iter 26 — per-filter date-preset chip library. When present, the
   * picker shows ONE chip per preset (in order) instead of the
   * hardcoded HP fiscal fallback. Each preset's ``compute`` block
   * tells the engine (and the client-side mirror) how to resolve
   * the chip id to a date range.
   */
  presets?: DatePreset[] | null;
  /**
   * Snap granularity of the dual-handle slider (``"day"`` | ``"week"``
   * | ``"month"``). Drives the standalone ``date_slider`` widget AND
   * (PR4) the slider row embedded in the ``date_range`` /
   * ``date_relative`` picker popover. Engine default is ``"day"``
   * when absent; the popover host falls back to ``"week"`` when the
   * config never serialized the field.
   */
  slider_step?: string | null;
  /**
   * Only for ``type: "range"``, how the slider labels, the inputs and
   * the active chip show the bounds: ``"number"`` | ``"percent"`` (the
   * column is a 0..1 fraction; the user types 30 for 30 %) |
   * ``"currency"``. Display only: the value keeps the column's units.
   */
  value_format?: string | null;
  /** Only for ``type: "range"``: ``"linear"`` (default) | ``"log"`` track. */
  slider_scale?: string | null;
  /** Only for ``type: "range"``: table id whose rows the histogram and
   *  "N left" count (``"main"`` = legacy table). Absent = no histogram. */
  histogram_table?: string | null;
}

/**
 * True when the filter type decodes a JSON date-window value
 * (``{"start","end"}`` / ``{"preset"}`` / ``{"relative"}``) on the
 * engine side — mirror of ``dashboard_engine._DATE_WINDOW_TYPES``.
 * Host pages use this instead of ``=== "date_range"`` literals for the
 * shared seeding / cascade-skip / clear-sentinel semantics.
 */
export function isDateWindowType(t?: string | null): boolean {
  return t === "date_range" || t === "date_relative" || t === "date_slider";
}

/**
 * Concrete window a ``DatePresetCompute`` resolves to — the response
 * shape of the builder's ``POST /api/builder/preview/date-preset``.
 */
export interface ResolvedDateWindow {
  start: string;
  end: string;
  days: number;
}

/**
 * Strict runtime guard for ``ResolvedDateWindow``. A 2xx whose body is
 * null / a string / a partial object must FAIL the caller loudly (per
 * the no-fabricated-data rule) instead of flowing into the UI as
 * "undefined → undefined". ``days`` must be a finite number and both
 * date strings non-empty.
 */
export function isResolvedDateWindow(raw: unknown): raw is ResolvedDateWindow {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return false;
  }
  const r = raw as { start?: unknown; end?: unknown; days?: unknown };
  return (
    typeof r.start === "string" &&
    r.start.length > 0 &&
    typeof r.end === "string" &&
    r.end.length > 0 &&
    typeof r.days === "number" &&
    Number.isFinite(r.days)
  );
}

/** A single quick-pick chip definition above the calendar. */
export interface DatePreset {
  id: string;
  label?: DisplayLabel | null;
  description?: DisplayLabel | null;
  compute: DatePresetCompute;
}

/** Discriminated-union recipe for how a preset resolves. */
export type DatePresetCompute =
  | { kind: "absolute_range"; params: { start: string; end: string } }
  | { kind: "relative_days"; params: { length: number } }
  | { kind: "relative_months"; params: { length: number } }
  | {
      kind: "hp_fiscal_q";
      params: {
        offset_back: number;
        duration_quarters: number;
        include_open: boolean;
      };
    };

// ---------------------------------------------------------------------------
// Dashboard composition types (shared by the DashboardBody / DrillSection /
// RowDrillPanel render layer). These are the JSON shapes the engine's
// ``fetch_*`` functions emit — the single contract both apps render from.
// Each app's API-response types (portal ``Report*`` in ``lib/api.ts``; the
// builder's preview response types) alias/conform to these (no cross-app
// imports). Bilingual fields are typed ``DisplayLabel`` (string | {en,es});
// the render layer resolves them via ``resolveLocalized``.
// ---------------------------------------------------------------------------

/** A computed KPI tile value + its display knobs (engine ``fetch_kpis`` row). */
export interface Kpi {
  /** May be bilingual ``{en, es}``; resolved by the render layer. */
  label: DisplayLabel;
  /** The render layer always runs this through ``formatCellValue`` (never
   *  arithmetic), so a text KPI value (string) is accepted alongside numbers. */
  value: number | string | null;
  format: "number" | "percent" | "currency";
  variant?: string | null;
  max?: number | null;
  gradient?: string | null;
  icon?: string | null;
  icon_bg?: string | null;
  spark_data?: number[] | null;
  previous?: number | null;
  severity?: string | null;
  description?: DisplayLabel | null;
  expected?: number | null;
  breakdown?: Array<{ label: string; value: number }> | null;
  /** May be bilingual ``{en, es}`` (engine ``_label``); resolved by the
   *  render layer before display — never pass raw to a string op. */
  unit?: DisplayLabel | null;
  width?: number;
  order?: number;
  goal_direction?: "up" | "down";
  /** DSA strip (`core` variant): badge pill text (bilingual), a static
   *  config knob echoed by the engine. */
  badge?: DisplayLabel | null;
  /** Authoring-config fields stamped onto the flattened item. */
  section_id?: string;
  section_title?: DisplayLabel;
  section_subtitle?: DisplayLabel;
  /** Tooltip text for the section header (config ``sections[].info``). */
  section_info?: DisplayLabel;
  /** Tooltip text for this KPI's label (config ``props.info``). */
  info?: DisplayLabel;
  /** Threshold bands that tint the VALUE (epic v3 scorecards).
   *  Authored descending with a catch-all last — the engine validates
   *  that at parse time, because the frontend resolver deliberately
   *  never throws. Absent means the value keeps its default colour: a
   *  bare number never implies a tone. */
  value_thresholds?: Array<{
    /** `null` = catch-all (JSON has no -Infinity). */
    min: number | null;
    tone: ConfigTone;
  }>;
  /** Coloured caption line under the value (epic v3) — the mockups'
   *  "Primary health driver" / "Mercado Livre · Supplies" tags. */
  caption?: {
    text: DisplayLabel;
    tone?: ConfigTone;
  } | null;
  /** Bottom action band ("Ver en Master Matrix →"). Without `href` it
   *  renders DISABLED — dashed border, muted, not focusable — instead of
   *  as a live button: a button that navigates nowhere teaches the reader
   *  to distrust the page. `note` says why and travels as the title.
   *  Costs ~30px, so a KPI declaring one usually needs one more row unit
   *  of `height`. */
  action?: {
    label: DisplayLabel;
    href?: string | null;
    note?: DisplayLabel | null;
    /** In-app target: open this SIBLING report of the report group (D8)… */
    report?: string | null;
    /** …and write these report filters on the way ("" clears one). */
    filters?: Record<string, string> | null;
  } | null;
  /** When set the KPI is clickable and opens this drill. */
  drill_target?: string | null;
  /** Operator drawn in the gap before this card ("×", "="): a KPI chain
   *  such as coverage × availability = presence reads as one identity. */
  joiner?: string | null;
  /** The report's highlighted metric: a static focus ring that steps
   *  back while another KPI's drill is open. */
  focused?: boolean | null;
}

/** Per-chart styling/format metadata (engine chart config). Used as a
 *  by-title styling lookup in the drill panels (the chart's own ``type`` comes
 *  from its data row, so ``type`` here is optional). */
/** One state of a saved-views portfolio (label + tone), by config. */
export interface PortfolioStateDef {
  label: DisplayLabel;
  tone: ConfigTone;
}

/**
 * The ``portfolio`` block of a ``saved_views_portfolio`` chart (DSA): which
 * report KPIs each card shows, which KPI value yields a view's state and
 * the bands that map it, which KPI names the leader, where a card click
 * jumps. Mirror of ``report_engine.discovery._parse_portfolio``'s output.
 */
export interface SavedViewsPortfolioConfig {
  card_kpis: string[];
  status: {
    kpi: string;
    thresholds: Array<{ max: number | null; state: string }>;
    none_state: string;
    lead_states: string[];
    behind_states: string[];
    states: Record<string, PortfolioStateDef>;
  };
  winner?: { kpi: string; source: "caption" | "value" } | null;
  action?: { label?: DisplayLabel | null; report?: string | null } | null;
}

export interface ChartConfig {
  type?: string;
  title: DisplayLabel;
  /** Threshold bands for the scorecard arrangement types (epic v3) —
   *  authored in the report config, never inferred from a value. */
  thresholds?: Array<{ min: number | null; tone: ConfigTone }>;
  /** Render the authored bands as a legend line (epic v3) — same
   *  array that tints the meters, so the two cannot drift. */
  bands_legend?: boolean;
  /** Secondary line under the chart title (scorecard arrangements). */
  subtitle?: DisplayLabel;
  /** Render the authored ``subtitle`` inside the card body.
   *  Opt-in: the card header is sized at exactly
   *  ``CHART_CARD_CHROME_PX``, so a second header line would push
   *  every existing chart's plot past its grid cell. Give the chart
   *  one extra row unit of height when turning this on. */
  subtitle_inline?: boolean;
  /** Densidad de fila de las barras rankeadas (`bar_horizontal`):
   *  `comfortable` (default) | `compact`. En compacta la fila baja de
   *  28 a 19px y el chip de rank numerado desaparece — el orden de las
   *  filas ya ES el ranking, y sus 28px vuelven al canal de labels.
   *  Siete filas pasan de 212px de alto natural a 149. */
  density?: string | null;
  /** Share-of-total % junto al valor en las barras rankeadas.
   *  Por defecto ON. Apagalo cuando los valores NO son partes de un
   *  todo: un indice (mercado = 100), un precio promedio, un ratio o
   *  un puntaje no son aditivos, y el % del total no corresponde a
   *  ninguna cantidad real. */
  show_share?: boolean | null;
  /** Share-bar segments for ``metric_card_grid`` rows (epic v3).
   *  Order and tones are AUTHORED (no tone = a category, painted from
   *  the series palette); each row supplies one ``seg_<key>`` column
   *  per segment. */
  segments?: Array<{ key: string; label: string; tone?: ConfigTone | null }>;
  /** Cards per row for ``metric_card_grid`` (1..4, default 4). */
  card_columns?: number | null;
  /** Meter denominator (default 100). */
  meter_max?: number | null;
  /** The filter this chart's clicks SET (``kpi_period_tiles``). Each
   *  tile is a value of this filter; the active tile reflects its
   *  current value; clicking toggles it. Empty ⇒ not clickable. */
  cross_filter_key?: string | null;
  /** Drill-swap (ronda 7): when set to a filter key, this chart renders
   *  IN PLACE of the chart whose ``cross_filter_key`` equals this key —
   *  only while that key holds an active value. It never occupies its
   *  own grid cell. */
  swap_on?: string | null;
  /** Click on a row (DSA prototype v7): ranked_list "Ver", a
   *  bar_horizontal bar, a heatmap cell. Opens ``report`` (a sibling of
   *  the group), writes the row's ``action_value`` into ``filter_key``
   *  and each ``{filter: column}`` of ``filter_map`` from the clicked row. */
  row_action?: {
    label: DisplayLabel;
    report?: string | null;
    filter_key?: string | null;
    filter_map?: Record<string, string> | null;
  } | null;
  /** ``ranked_list`` link under the list — the KPI action shape. */
  footer_action?: {
    label: DisplayLabel;
    href?: string | null;
    report?: string | null;
    filters?: Record<string, string> | null;
  } | null;
  /** ``ranked_list`` rank slot: number (default) | up | down | none. */
  rank_marker?: string | null;
  /** In-chart controls: chip groups in the card header; the chosen value
   *  reaches THIS chart's SQL as ``{filter:<key>}``. */
  controls?: Array<{
    key: string;
    label?: DisplayLabel | null;
    options: Array<{ value: string; label: DisplayLabel }>;
    default: string;
    /** Shown only while every named control holds that value. */
    visible_when?: Record<string, string> | null;
  }> | null;
  /** ``multi_line``: legend entries hide / show their series. */
  legend_toggle?: boolean | null;
  /** Text instead of the generic "No data" when the SQL returns no rows. */
  empty_message?: DisplayLabel | null;
  /** ``saved_views_portfolio`` only (DSA): the block the host computes
   *  the element from. See ``SavedViewsPortfolioConfig``. */
  portfolio?: SavedViewsPortfolioConfig | null;
  dimension?: string | null;
  metric?: string | null;
  metrics?: Array<{ label: string; sql: string }>;
  x?: string | null;
  y?: string | null;
  size?: string | null;
  sort?: string | null;
  limit?: number | null;
  tone?: ConfigTone | null;
  tones?: ConfigTone[];
  stacked?: boolean;
  label_column?: string | null;
  x_format?: "currency" | "percent" | "number" | null;
  y_format?: "currency" | "percent" | "number" | null;
  data_labels?: {
    show: boolean;
    format?: "raw" | "compact" | "currency" | "percent";
    decimals?: number;
  } | null;
  axis_format?: {
    x?: { format?: "raw" | "compact" | "currency" | "percent"; decimals?: number } | null;
    y?: {
      format?: "raw" | "compact" | "currency" | "percent";
      decimals?: number;
      min?: number;
      max?: number;
      step?: number;
      suffix?: string;
    } | null;
    y1?: {
      format?: "raw" | "compact" | "currency" | "percent";
      decimals?: number;
      min?: number;
      max?: number;
      step?: number;
      suffix?: string;
    } | null;
  } | null;
  dual_axis?: boolean;
  ignore_filters?: string[];
  width?: number;
  /** Legacy grid row-span 1..3. Deprecated by ``height`` (uniform-grid)
   *  but still read for compat: absent ``height`` + ``row_span > 1``
   *  maps to ``DEFAULT_H_CHART × row_span`` (see ``grid-layout.ts``). */
  row_span?: number;
  /** Grid height in ROW UNITS (uniform grid: 40px units, 16px gap —
   *  px = h*40 + (h-1)*16). The engine always serializes it (PR-1);
   *  builder-side flattening may omit it → resolved by
   *  ``resolveHeight`` with the mirrored DEFAULT_H table. */
  height?: number;
  order?: number;
  section_id?: string;
  section_title?: DisplayLabel;
  section_subtitle?: DisplayLabel;
  /** Tooltip text for the section header (config ``sections[].info``). */
  section_info?: DisplayLabel;
  /** Tooltip text for this chart's title (config ``props.info``). */
  info?: DisplayLabel;
  /** Legend delta chips on/off (default on) — multi_line opt-out. */
  legend_deltas?: boolean;
  /** Tooltip "Total" row on/off (default on) — multi_line opt-out for
   *  series that are not parts of a whole (prices per retailer). */
  tooltip_total?: boolean;
  table_columns?: Array<{
    key: string;
    column: string;
    label: string;
    format: string | null;
    badge: string | null;
    /** Per-column cell render mode (``"trend"`` = rich comparison cell). */
    render?: string | null;
  }>;
  sort_expr?: string | null;
  map_variant?: "south_america" | "chile" | string | null;
  year?: number | string | null;
  months_csv?: string | null;
  /** Authoring item id (builder inspector; absent in the portal). */
  item_id?: string;
}

/** A chart's executed data (engine ``fetch_charts`` result). */
export interface ChartData {
  type: string;
  title: DisplayLabel;
  data: Array<Record<string, unknown>>;
  error?: string;
}

/** Paginated table result (engine ``fetch_table_data``). */
export interface TableData {
  rows: Array<Record<string, unknown>>;
  total_count: number;
  /** Pivot tables (DSA 1.20): the dynamic columns the engine discovered
   *  for this request, in order; keys match the row dict. */
  pivot_columns?: Array<{ key: string; label: string }>;
}

/** A named-table config entry (``dashboard.tables[]``). */
export interface TableColumn {
  column: string;
  label: string;
  format: string | null;
  key?: string;
  hidden?: boolean;
  /** Cross-filter target. When set (and it names an eligible report
   *  filter), cells in this column are clickable and set that filter
   *  to the cell's raw value. Empty/absent = not filterable. */
  filter_key?: string | null;
  /** Per-column cell render mode (``ColumnRender``): ``heat`` /
   *  ``sparkline`` / ``status`` / ``progress`` / ``compare`` /
   *  ``trend`` / ``chip``. Absent = default cell. */
  render?: string | null;
  /** Key of another column in the same row, rendered as a muted second
   *  line under this cell ("38" over "de 209 en el retailer"). Pair it
   *  with ``hidden: true`` on the referenced column so the figure and
   *  its denominator read as one cell instead of two headers. */
  sub_key?: string | null;
  /** Congela la columna al scrollear horizontal. Solo un PREFIJO de
   *  columnas puede congelarse (las de identidad): una columna fija en
   *  el medio dejaria un hueco. Los offsets se miden del layout real. */
  sticky?: boolean | null;
  /** Ancho maximo de la columna en px; lo que no entra se corta con
   *  elipsis y el valor entero queda en el tooltip nativo. Pensado
   *  para las congeladas: sin techo, un nombre de producto largo pide
   *  447px y la franja fija tapa las columnas que el scroll deberia
   *  revelar. */
  max_width?: number | null;
  /** Formula line under the header label ("listados / universo"): the
   *  denominator a percentage uses, readable without a tooltip. */
  sub_label?: DisplayLabel | null;
  /** Key of the row's denominator for a ``fraction`` cell ("67 / 96"
   *  plus "70 %" on a muted line). Names a key in the ROW. */
  denominator_key?: string | null;
  /** Parts of a ``mini_stack`` cell: one 100%-wide bar split by the
   *  values of these row keys, in this order, each with its tone (no
   *  tone = a category, painted from the series palette). */
  stack?: Array<{ key: string; label?: DisplayLabel | null; tone?: ConfigTone | null }> | null;
  /** ``state_cell`` map: SQL token → how the cell shows it. Authored in
   *  the config; the render owns no project state. */
  states?: Record<string, StateSpec> | null;
  /** Key of another column of the row shown as a small pill after the
   *  value ("Hisense [YOUR BRAND]"); empty value = no pill. */
  badge_key?: string | null;
  /** ``state_cell`` look: ``pill`` (default) or ``fill`` (whole cell
   *  tinted with a tone border on the left). */
  cell_style?: string | null;
}

/** One authored state of a ``state_cell`` column. */
export interface StateSpec {
  label: DisplayLabel;
  tone: "good" | "warn" | "bad" | "neutral" | string;
  glyph?: string | null;
  /** Template for a numeric suffix (``"fallen:12"``): ``"−{n} d"``. */
  suffix?: DisplayLabel | null;
  /** Muted lines under the pill. A suffix may carry several values
   *  (``"yes:67|91|59"``): templates read them as ``{1}``, ``{2}``… and
   *  ``{n}`` is the whole suffix. */
  lines?: DisplayLabel[] | null;
  /** The pill shows the label ("Yes") instead of the glyph. */
  show_label?: boolean | null;
}

export interface TableConfig {
  id: string;
  title: DisplayLabel;
  columns: TableColumn[];
  default_sort?: string | null;
  sort_dir?: string;
  page_size?: number;
  ignore_filters?: string[];
  width?: number;
  /** Legacy grid row-span 1..3 — deprecated by ``height`` (see
   *  ``ChartConfig.row_span``). */
  row_span?: number;
  /** Grid height in ROW UNITS (see ``ChartConfig.height``). */
  height?: number;
  order?: number;
  section_id?: string;
  section_title?: DisplayLabel;
  section_subtitle?: DisplayLabel;
  /** Tooltip text for the section header (config ``sections[].info``). */
  section_info?: DisplayLabel;
  /** Tooltip text for this table's title (config ``props.info``). */
  info?: DisplayLabel;
  /** Row-drill target drill_id; when set, rows are clickable. */
  row_drill_target?: string | null;
  /** Column whose value scopes the row drill (defaults to first column). */
  row_drill_param?: string;
  /** How the row drill is shown (see ``row-drill-mode.ts``), chosen
   *  per report: ``"swap"`` (default when absent) replaces the cell's
   *  content with the panel, with the slide; ``"accordion"`` (opt-in)
   *  expands the detail UNDER the clicked row inside the table's own
   *  scrolling zone, the row still visible. In both the grid cell never
   *  changes size. Any other value is a config error the render
   *  surfaces as a visible error card. */
  row_drill_mode?: "accordion" | "swap" | null;
  hidden?: boolean;
  /** Key of a boolean column flagging the "Total" row: pinned at the
   *  bottom, bold, outside the zebra, whatever the sort. */
  total_row_key?: string | null;
  /** Key of a column flagging rows to highlight (tinted, they keep
   *  their place in the sort): a boolean tints neutral, a tone
   *  (good / warn / bad) tints with that tone. */
  highlight_row_key?: string | null;
  /** Row-dict fields that identify ONE row across sorts, pages and
   *  reloads (tasks). Empty / absent = no report flag on its rows. */
  row_identity?: string[];
  /** Quick-filter chips above the table; the active one reaches THIS
   *  table's SQL as ``{filter:<key>}`` (``""`` = all). */
  chips?: {
    key: string;
    options: Array<{ value: string; label: DisplayLabel }>;
  } | null;
  /** Sort chips above the table (a declared column key each). */
  sort_presets?: Array<{ label: DisplayLabel; sort_by: string; sort_dir: "asc" | "desc" }> | null;
  /** Dynamic columns per distinct value of ``dimension`` (DSA 1.20);
   *  every discovered column renders with ``render``. */
  pivot?: {
    dimension: string;
    value: string;
    render?: string | null;
    limit?: number;
    /** State map shared by every discovered column (``state_cell``). */
    states?: Record<string, StateSpec> | null;
    /** Declared column key the discovered columns follow ("" = end). */
    insert_after?: string | null;
    /** ``state_cell`` look of every discovered column. */
    cell_style?: string | null;
  } | null;
  /** Authoring item id (builder inspector; absent in the portal). */
  item_id?: string;
}

/** A text/markdown element value (engine ``fetch_texts``). */
export interface TextValue {
  /** As authored: plain or bilingual ``{en, es}``. */
  label: DisplayLabel;
  value: string | number | null;
  error?: string;
}

/** KPI-drill payload (engine ``fetch_drill``). */
export interface DrillData {
  drill_id: string;
  title?: DisplayLabel | null;
  description?: DisplayLabel | null;
  kpis: Kpi[];
  charts: ChartData[];
}

/** Row-drill payload (engine ``fetch_table_row_drill``). */
export interface RowDrillData {
  drill_id: string;
  title?: DisplayLabel | null;
  description?: DisplayLabel | null;
  /** Optional — the render layer displays the clicked value via the
   *  ``rowValue`` prop, not this field (lets both apps' responses conform). */
  row_value?: string;
  charts: ChartData[];
  table?:
    | (TableData & {
        error?: string;
        /** Formato declarado por columna, keyed por el MISMO alias con
         *  el que vienen las filas (el label). Viaja con los datos
         *  porque el panel recibe el config de columnas de la tabla
         *  PADRE, no el del drill: sin esto rendea todo numero como
         *  `number` y una brecha de 0.111 se ve "0.1". */
        formats?: Record<string, string | null>;
        /** Campos que identifican UNA fila (tareas), con el mismo
         *  alias que las filas. Vacio = sin bandera de reporte. */
        row_identity?: string[];
      })
    | null;
}

/** A KPI config entry (``dashboard.kpis[]``) — drives layout + drill, matched
 *  by index to the fetched ``Kpi`` data. */
export interface KpiConfig {
  label: DisplayLabel;
  sql?: string;
  format?: string;
  ignore_filters?: string[];
  drill_target?: string | null;
  width?: number;
  /** Coloca este KPI DENTRO del grid de su sección, como una celda
   *  más, en vez de la tira horizontal que va arriba.
   *
   *  La tira ocupa todo el ancho, así que sin esto un reporte no puede
   *  poner sus métricas EN COLUMNA al costado de una tabla — que es la
   *  forma del reporte de referencia del Pricing-Brand: la tabla
   *  grande a la izquierda, el resumen apilado a la derecha. Dentro
   *  del grid mandan el orden de los items y el empaquetado, y el
   *  `width` del KPI pasa a contar en las 12 columnas. */
  in_grid?: boolean | null;
  /** Computed but not drawn (engine ``KpiConfig.hidden``, same contract as
   *  a hidden table): its payload entry keeps the index pairing, the body
   *  and the skeleton skip it. The KPI copies a report group's
   *  saved-views portfolio reads are hidden in the portfolio's report. */
  hidden?: boolean | null;
  /** Grid row-span (config parity — the KPI grid currently ignores it). */
  row_span?: number;
  /** Grid height in row units (config parity — the KPI strip ignores it,
   *  same as ``row_span``; see ``ChartConfig.height``). */
  height?: number;
  order?: number;
  section_id?: string;
  section_title?: DisplayLabel;
  section_subtitle?: DisplayLabel;
  /** Tooltip text for the section header (config ``sections[].info``). */
  section_info?: DisplayLabel;
  /** Tooltip text for this KPI's label (config ``props.info``). */
  info?: DisplayLabel;
  goal_direction?: "up" | "down";
  /** Authoring item id — stamped by the builder's flattener so the
   *  Dashboard-tab inspector can select this item. Absent in the portal. */
  item_id?: string;
}

/** A text element config entry (``dashboard.texts[]``). */
export interface TextConfig {
  label?: DisplayLabel;
  section_id?: string;
  section_title?: DisplayLabel;
  section_subtitle?: DisplayLabel;
  /** Tooltip text for the section header (config ``sections[].info``). */
  section_info?: DisplayLabel;
  order?: number;
  /** Grid height in row units (default 1 — see ``grid-layout.ts``). */
  height?: number;
  /** Authoring item id (builder inspector; absent in the portal). */
  item_id?: string;
}

/** The flattened engine-shape config the shared ``DashboardBody`` consumes
 *  (the portal's native shape; the builder adapts via ``flattenForEngine``). */
/** One tab of the tab bar. Since PR-3 (report groups, 2026-10) the bar
 *  only shows the reports of a group (``id`` = the member key); a report
 *  has no tabs of its own. */
export interface DashboardTab {
  id: string;
  label?: DisplayLabel | null;
  /** Signature colour (hex) of the active state; empty = accent. */
  color?: string | null;
  section_ids: string[];
}

export interface DashboardRenderConfig {
  filters: DashboardFilter[];
  kpis: KpiConfig[];
  charts: ChartConfig[];
  tables?: TableConfig[];
  /** Legacy singular table (dashboard_explorer) — rendered when ``tables`` is
   *  empty. */
  table?: TableConfig;
  texts?: TextConfig[];
  drills?: DrillConfig[];
  /** Dashboard-level tone bands that scorecard chart blocks inherit when they
   *  don't declare their own per-block ``thresholds``. One editable place for
   *  the whole report's colour cut-offs. */
  thresholds?: Array<{ min: number | null; tone: ConfigTone }>;
  /** Human note describing the dashboard-level thresholds (display-only). */
  thresholds_note?: string;
  /** Glossary drawer (DSA 1.16): a button next to the tabs opens it. */
  glossary?: DashboardGlossary | null;
}

/** A drill definition in the dashboard config (``dashboard.drills[]``). */
export interface DrillConfig {
  drill_id: string;
  title?: DisplayLabel | null;
  description?: DisplayLabel | null;
  charts: ChartConfig[];
  kpis: Array<{ label: DisplayLabel; format?: string; variant?: string }>;
  nested_table_id?: string | null;
  nested_drill_target?: string | null;
  nested_drill_param?: string | null;
  /** Optional row-drill panel composition override (in-place swap):
   *  ``"split"`` = charts left / table right, ``"split_chart_right"`` =
   *  table left / charts in a 30 % column on the right, ``"stack"`` =
   *  charts on top / table below. Absent → automatic by the hosting
   *  cell's column span (≥ 8 → split, else stack). */
  drill_layout?: DrillLayout | null;
}

/** The drill panel compositions — mirror of the engine's
 *  ``report_engine.models.DRILL_LAYOUTS``. */
export const DRILL_LAYOUTS = ["stack", "split", "split_chart_right"] as const;
export type DrillLayout = (typeof DRILL_LAYOUTS)[number];

// Re-export the localized-string types that DashboardFilter
// references — saves consumers a second import.
export type { DisplayLabel, LocalizedString };

/** Authored glossary of a dashboard (DSA 1.16). Every text bilingual. */
export interface DashboardGlossary {
  title?: DisplayLabel | null;
  subtitle?: DisplayLabel | null;
  sections: Array<{
    title: DisplayLabel;
    intro?: DisplayLabel | null;
    entries: Array<{ term: DisplayLabel; definition: DisplayLabel; formula?: DisplayLabel | null }>;
  }>;
}
